/**
 * Permisos por acción de un usuario (2026-10-07, docs/PERMISOS-CATALOGO.md).
 * Cuatro pestañas según el alcance del permiso: General (globales), Unidades,
 * Sedes (bots) y Concesionarias. Lo que quien edita no tiene, no lo puede
 * dar ni quitar (el backend lo vuelve a verificar).
 */
import { rec_Fo as usarConfirmacion, rec_React as React, rec_api as api } from "../../runtime/index.jsx";
import "./permisos.css";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useMemo = (...a) => React.useMemo(...a);
const useState = (...a) => React.useState(...a);

const clave = (codigo, ambitoId) => `${codigo}|${ambitoId ?? ``}`;
const PESTANAS = [
  { id: `general`, label: `General` },
  { id: `unidades`, label: `Unidades` },
  { id: `sedes`, label: `Sedes (bots)` },
  { id: `concesionarias`, label: `Concesionarias` },
];
const ATAJOS = [
  { id: `TOTAL`, label: `Acceso total` },
  { id: `COLABORADOR`, label: `Colaborador` },
  { id: `LECTURA`, label: `Sólo lectura` },
];
const NOMBRE_ATAJO = { TOTAL: `Total`, COLABORADOR: `Colaborador`, LECTURA: `Lectura` };

/**
 * Perfiles de Control de Labores (2026-10-31): un clic deja en la sede los
 * permisos de Control de Labores de ese papel (reemplaza los que tenía de ese
 * módulo). No tocan los del bot: su conexión la maneja sólo el dueño.
 */
const PERFILES_LABORES = [
  { id: `supervisor`, nombre: `Supervisor`, ayuda: `Arma su plan de la semana y elige a su personal cada día`,
    codigos: [`labores.control.ver`, `labores.asignaciones.gestionar`] },
  { id: `coordinador`, nombre: `Coordinador`, ayuda: `Valida los planes, cubre faltas, carga trabajadores y puntos, ve el reporte`,
    codigos: [`labores.control.ver`, `labores.asignaciones.gestionar`, `labores.planificacion.gestionar`, `labores.maestros.gestionar`, `labores.reporte.ver`] },
  { id: `jefe`, nombre: `Jefe SSCC`, ayuda: `Ve la vista general, el avance y el registro de evidencias`,
    codigos: [`labores.control.ver`, `labores.reporte.ver`] },
];

/** módulo → grupo → items, en el orden del catálogo. */
function agrupar(items) {
  const modulos = new Map();
  for (const it of items) {
    if (!modulos.has(it.modulo)) modulos.set(it.modulo, new Map());
    const grupos = modulos.get(it.modulo);
    if (!grupos.has(it.grupo)) grupos.set(it.grupo, []);
    grupos.get(it.grupo).push(it);
  }
  return [...modulos].map(([modulo, grupos]) => ({ modulo, grupos: [...grupos].map(([grupo, items]) => ({ grupo, items })) }));
}

/** Unidades en orden de árbol, con su profundidad. */
function arbol(unidades) {
  const hijos = new Map();
  const ids = new Set(unidades.map((u) => u.id));
  for (const u of unidades) {
    const p = u.padreId != null && ids.has(u.padreId) ? u.padreId : null;
    if (!hijos.has(p)) hijos.set(p, []);
    hijos.get(p).push(u);
  }
  const out = [];
  const visitar = (padre, nivel) => {
    for (const u of hijos.get(padre) ?? []) {
      out.push({ ...u, nivel });
      visitar(u.id, nivel + 1);
    }
  };
  visitar(null, 0);
  return out;
}

function Casilla({ marcado, deshabilitado, onCambio, children, titulo }) {
  return (
    <label className={`pm-casilla${marcado ? ` pm-on` : ``}${deshabilitado ? ` pm-off` : ``}`} title={titulo}>
      <input type="checkbox" checked={marcado} disabled={deshabilitado} onChange={(e) => onCambio(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

/** Bloque de módulos → grupos → casillas, para un ámbito dado (null = global). */
function Matriz({ modulos, ambitoId, marcados, puedoDar, alternar }) {
  return modulos.map(({ modulo, grupos }) => (
    <section key={modulo} className="pm-modulo">
      <h4>{modulo}</h4>
      {grupos.map(({ grupo, items }) => (
        <div key={grupo} className="pm-grupo">
          <span className="pm-grupo-nombre">{grupo}</span>
          <div className="pm-acciones">
            {items.map((it) => {
              const k = clave(it.codigo, ambitoId);
              return (
                <Casilla key={it.codigo} marcado={marcados.has(k)} deshabilitado={!puedoDar(it.codigo, ambitoId)}
                  titulo={puedoDar(it.codigo, ambitoId) ? it.codigo : `No lo tenés: no podés darlo ni quitarlo`}
                  onCambio={(v) => alternar([k], v)}>
                  {it.etiqueta}
                </Casilla>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  ));
}

export function PermisosUsuario({ usuario, onClose }) {
  const { confirmar } = usarConfirmacion();
  const [catalogo, setCatalogo] = useState(null);
  const [ambitos, setAmbitos] = useState(null);
  const [mios, setMios] = useState(null);
  const [original, setOriginal] = useState(new Set());
  const [marcados, setMarcados] = useState(new Set());
  const [superadmin, setSuperadmin] = useState(false);
  const [superOriginal, setSuperOriginal] = useState(false);
  const [pestana, setPestana] = useState(`unidades`);
  const [unidadSel, setUnidadSel] = useState(null);
  const [sedeSel, setSedeSel] = useState(null);
  const [filtro, setFiltro] = useState(``);
  const [soloMarcadas, setSoloMarcadas] = useState(false);
  const [error, setError] = useState(``);
  const [estado, setEstado] = useState(``);
  const [guardando, setGuardando] = useState(false);

  const cargarUsuario = (d) => {
    const s = new Set(d.filas.map((f) => clave(f.permiso, f.ambitoId)));
    setOriginal(s);
    setMarcados(new Set(s));
    setSuperadmin(d.superadmin);
    setSuperOriginal(d.superadmin);
  };

  useEffect(() => {
    Promise.all([api(`/permisos/catalogo`), api(`/permisos/ambitos`), api(`/permisos/mios`), api(`/permisos/usuarios/${usuario.id}`)])
      .then(([c, a, m, d]) => {
        setCatalogo(c);
        setAmbitos(a);
        setMios(m);
        cargarUsuario(d);
        setSedeSel(a.sedes.find((s) => s.nombre === `ICA`)?.id ?? a.sedes[0]?.id ?? null);
      })
      .catch((e) => setError(e.message));
  }, [usuario.id]);

  const porAmbito = useMemo(() => {
    const m = { GLOBAL: [], UNIDAD: [], SEDE: [], CONCESIONARIA: [] };
    for (const it of catalogo ?? []) m[it.ambito].push(it);
    return m;
  }, [catalogo]);
  const modulos = useMemo(() => ({
    GLOBAL: agrupar(porAmbito.GLOBAL),
    UNIDAD: agrupar(porAmbito.UNIDAD),
    SEDE: agrupar(porAmbito.SEDE),
    CONCESIONARIA: agrupar(porAmbito.CONCESIONARIA),
  }), [porAmbito]);
  const unidades = useMemo(() => arbol(ambitos?.unidades ?? []), [ambitos]);

  const puedoDar = (codigo, ambitoId) => {
    if (!mios) return false;
    if (mios.superadmin) return true;
    if (mios.globales.includes(codigo)) return true;
    return (mios.porAmbito[codigo] ?? []).includes(ambitoId);
  };

  const alternar = (claves, valor) => {
    setMarcados((prev) => {
      const s = new Set(prev);
      for (const k of claves) {
        const [codigo, a] = k.split(`|`);
        if (!puedoDar(codigo, a === `` ? null : Number(a))) continue;
        if (valor) s.add(k);
        else s.delete(k);
      }
      return s;
    });
    setEstado(``);
  };

  /** Códigos de un ámbito marcados (para atajos y conteos). */
  const codigosEn = (lista, ambitoId) => lista.filter((it) => marcados.has(clave(it.codigo, ambitoId))).map((it) => it.codigo);
  const atajoDe = (ambitoId) => {
    const tiene = new Set(codigosEn(porAmbito.UNIDAD, ambitoId));
    if (!tiene.size) return null;
    for (const a of ATAJOS) {
      const del = porAmbito.UNIDAD.filter((it) => it.atajos.includes(a.id)).map((it) => it.codigo);
      if (del.length === tiene.size && del.every((c) => tiene.has(c))) return NOMBRE_ATAJO[a.id];
    }
    return `Personalizado`;
  };
  const aplicarAtajo = (ambitoId, atajo) => {
    const todas = porAmbito.UNIDAD.map((it) => clave(it.codigo, ambitoId));
    alternar(todas, false);
    if (atajo) alternar(porAmbito.UNIDAD.filter((it) => it.atajos.includes(atajo)).map((it) => clave(it.codigo, ambitoId)), true);
  };
  const descendientes = (id) => {
    const out = [];
    const i = unidades.findIndex((u) => u.id === id);
    if (i < 0) return out;
    for (let j = i + 1; j < unidades.length && unidades[j].nivel > unidades[i].nivel; j++) out.push(unidades[j].id);
    return out;
  };
  const copiarASubunidades = (id) => {
    const mias = new Set(codigosEn(porAmbito.UNIDAD, id));
    for (const d of descendientes(id)) {
      alternar(porAmbito.UNIDAD.map((it) => clave(it.codigo, d)), false);
      alternar([...mias].map((c) => clave(c, d)), true);
    }
  };

  const cambios = useMemo(() => {
    let n = 0;
    for (const k of marcados) if (!original.has(k)) n++;
    for (const k of original) if (!marcados.has(k)) n++;
    return n + (superadmin !== superOriginal ? 1 : 0);
  }, [marcados, original, superadmin, superOriginal]);

  const guardar = async () => {
    setGuardando(true);
    setError(``);
    try {
      const filas = [...marcados].map((k) => {
        const [permiso, a] = k.split(`|`);
        return { permiso, ambitoId: a === `` ? null : Number(a) };
      });
      const d = await api(`/permisos/usuarios/${usuario.id}`, {
        method: `PUT`,
        body: JSON.stringify({ filas, superadmin: mios?.superadmin ? superadmin : null }),
      });
      cargarUsuario(d);
      setEstado(`Guardado`);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = async () => {
    if (cambios && !(await confirmar({ titulo: `Cambios sin guardar`, mensaje: `Hay ${cambios} cambios sin guardar. ¿Salir igual?`, confirmarTexto: `Salir`, peligro: true }))) return;
    onClose();
  };

  const conteo = (lista, ambitoId) => codigosEn(lista, ambitoId).length;
  const textoFiltro = filtro.trim().toLowerCase();
  const unidadesVisibles = unidades.filter((u) =>
    (!textoFiltro || u.nombre.toLowerCase().includes(textoFiltro)) && (!soloMarcadas || conteo(porAmbito.UNIDAD, u.id) > 0));
  const unidad = unidades.find((u) => u.id === unidadSel);

  const cargando = !catalogo || !ambitos || !mios;
  const nombre = `${usuario.nombre ?? ``} ${usuario.apellido ?? ``}`.trim() || usuario.usuario;

  return (
    <div className="pm-fondo" role="dialog" aria-modal="true" aria-label={`Permisos de ${nombre}`}>
      <div className="pm-panel">
        <header className="pm-cabecera">
          <div className="pm-titulo">
            <h3>Permisos de {nombre}</h3>
            <span>{usuario.usuario}</span>
          </div>
          <label className={`pm-super${superadmin ? ` pm-on` : ``}`} title={mios?.superadmin ? `` : `Sólo un superadmin lo cambia`}>
            <input type="checkbox" checked={superadmin} disabled={!mios?.superadmin} onChange={(e) => setSuperadmin(e.target.checked)} />
            Superadmin (puede todo)
          </label>
          <div className="pm-botones">
            {estado && !cambios && <span className="pm-ok">{estado}</span>}
            {cambios > 0 && <span className="pm-cambios">{cambios} cambios</span>}
            <button type="button" className="pm-btn" onClick={cerrar}>Cerrar</button>
            <button type="button" className="pm-btn pm-primario" disabled={!cambios || guardando} onClick={guardar}>
              {guardando ? `Guardando…` : `Guardar`}
            </button>
          </div>
        </header>

        {error && <p className="pm-error">{error}</p>}
        {superadmin && <p className="pm-aviso">Es superadmin: puede todo. Lo marcado abajo se usa sólo si se le quita el superadmin.</p>}

        <nav className="pm-pestanas">
          {PESTANAS.map((p) => (
            <button key={p.id} type="button" className={pestana === p.id ? `pm-activa` : ``} onClick={() => setPestana(p.id)}>{p.label}</button>
          ))}
        </nav>

        {cargando ? (
          <p className="pm-vacio">{error ? `` : `Cargando…`}</p>
        ) : pestana === `general` ? (
          <div className="pm-cuerpo pm-scroll">
            <p className="pm-ayuda">Permisos que no dependen de un área: lo propio de cada uno en el planner, reportar incidencias, organigrama y configuración.</p>
            <Matriz modulos={modulos.GLOBAL} ambitoId={null} marcados={marcados} puedoDar={puedoDar} alternar={alternar} />
          </div>
        ) : pestana === `unidades` ? (
          <div className="pm-cuerpo pm-dos">
            <aside className="pm-lista">
              <input className="pm-buscar" placeholder="Buscar unidad" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
              <label className="pm-solo"><input type="checkbox" checked={soloMarcadas} onChange={(e) => setSoloMarcadas(e.target.checked)} /> Sólo con permisos</label>
              <ul>
                {unidadesVisibles.map((u) => {
                  const n = conteo(porAmbito.UNIDAD, u.id);
                  const atajo = atajoDe(u.id);
                  return (
                    <li key={u.id}>
                      <button type="button" className={`${u.id === unidadSel ? `pm-sel` : ``}${n ? ` pm-con` : ``}`}
                        style={{ paddingLeft: `${0.5 + (textoFiltro || soloMarcadas ? 0 : u.nivel) * 0.9}rem` }} onClick={() => setUnidadSel(u.id)}>
                        <span className="pm-nombre">{u.nombre}</span>
                        {atajo && <span className={`pm-chip pm-chip-${atajo.toLowerCase()}`}>{atajo}</span>}
                      </button>
                    </li>
                  );
                })}
                {!unidadesVisibles.length && <li className="pm-vacio">Sin unidades</li>}
              </ul>
            </aside>
            <div className="pm-detalle pm-scroll">
              {!unidad ? (
                <p className="pm-vacio">Elegí una unidad. Marcar una unidad no incluye sus subunidades: cada una se marca aparte.</p>
              ) : (
                <>
                  <div className="pm-detalle-cab">
                    <h4>{unidad.nombre}</h4>
                    <div className="pm-atajos">
                      {ATAJOS.map((a) => (
                        <button key={a.id} type="button" className="pm-btn" onClick={() => aplicarAtajo(unidad.id, a.id)}>{a.label}</button>
                      ))}
                      <button type="button" className="pm-btn" onClick={() => aplicarAtajo(unidad.id, null)}>Quitar todo</button>
                      {descendientes(unidad.id).length > 0 && (
                        <button type="button" className="pm-btn" title="Copia lo marcado aquí a cada subunidad (reemplaza lo que tengan)"
                          onClick={() => copiarASubunidades(unidad.id)}>
                          Copiar a sus {descendientes(unidad.id).length} subunidades
                        </button>
                      )}
                    </div>
                  </div>
                  <Matriz modulos={modulos.UNIDAD} ambitoId={unidad.id} marcados={marcados} puedoDar={puedoDar} alternar={alternar} />
                </>
              )}
            </div>
          </div>
        ) : pestana === `sedes` ? (
          <div className="pm-cuerpo pm-dos">
            <aside className="pm-lista">
              <ul>
                {ambitos.sedes.map((s) => {
                  const n = conteo(porAmbito.SEDE, s.id);
                  return (
                    <li key={s.id}>
                      <button type="button" className={`${s.id === sedeSel ? `pm-sel` : ``}${n ? ` pm-con` : ``}`} onClick={() => setSedeSel(s.id)}>
                        <span className="pm-nombre">{s.nombre}</span>
                        {n > 0 && <span className="pm-chip">{n}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
            <div className="pm-detalle pm-scroll">
              {sedeSel != null && (
                <>
                  <div className="pm-detalle-cab">
                    <h4>{ambitos.sedes.find((s) => s.id === sedeSel)?.nombre}</h4>
                    <div className="pm-atajos">
                      <button type="button" className="pm-btn" onClick={() => alternar(porAmbito.SEDE.map((it) => clave(it.codigo, sedeSel)), true)}>Todo</button>
                      <button type="button" className="pm-btn" onClick={() => {
                        alternar(porAmbito.SEDE.map((it) => clave(it.codigo, sedeSel)), false);
                        alternar(porAmbito.SEDE.filter((it) => it.codigo.endsWith(`.ver`)).map((it) => clave(it.codigo, sedeSel)), true);
                      }}>Sólo ver</button>
                      <button type="button" className="pm-btn" onClick={() => alternar(porAmbito.SEDE.map((it) => clave(it.codigo, sedeSel)), false)}>Nada</button>
                    </div>
                  </div>
                  <div className="pm-perfiles">
                    <span className="pm-ayuda">Control de Labores, de un clic:</span>
                    {PERFILES_LABORES.map((pf) => {
                      const delModulo = porAmbito.SEDE.filter((it) => it.modulo === `Control de Labores`).map((it) => it.codigo);
                      const tiene = new Set(codigosEn(porAmbito.SEDE.filter((it) => it.modulo === `Control de Labores`), sedeSel));
                      // Aplicado: lo marcado de Control de Labores es justo lo del perfil.
                      const delPerfil = pf.codigos.filter((c) => delModulo.includes(c));
                      const activo = delPerfil.length === tiene.size && delPerfil.every((c) => tiene.has(c));
                      return (
                        <button key={pf.id} type="button" title={pf.ayuda} className={activo ? `pm-btn pm-btn-on` : `pm-btn`} onClick={() => {
                          alternar(delModulo.map((c) => clave(c, sedeSel)), false);
                          alternar(pf.codigos.map((c) => clave(c, sedeSel)), true);
                        }}>{pf.nombre}</button>
                      );
                    })}
                  </div>
                  <Matriz modulos={modulos.SEDE} ambitoId={sedeSel} marcados={marcados} puedoDar={puedoDar} alternar={alternar} />
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="pm-cuerpo pm-scroll">
            <p className="pm-ayuda">Para usuarios de una concesionaria: ven sólo las actas y observaciones de su empresa y pueden levantarlas. Los levantamientos quedan por aprobar del encargado de Alimentación.</p>
            {ambitos.concesionarias.map((c) => (
              <section key={c.id} className="pm-modulo">
                <h4>{c.nombre}</h4>
                <div className="pm-acciones">
                  {porAmbito.CONCESIONARIA.map((it) => (
                    <Casilla key={it.codigo} marcado={marcados.has(clave(it.codigo, c.id))} deshabilitado={!puedoDar(it.codigo, c.id)}
                      onCambio={(v) => alternar([clave(it.codigo, c.id)], v)}>
                      {it.etiqueta}
                    </Casilla>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
